<script setup lang="ts">
import QRCode from 'qrcode';
import jsQR from 'jsqr';
import { computed, onBeforeUnmount, ref, watch, watchEffect } from 'vue';
import type { ComponentPublicInstance } from 'vue';
import {
  Baby,
  Calendar,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Copy,
  ImagePlus,
  KeyRound,
  MapPin,
  QrCode,
  Save,
  ShieldCheck,
  Trash2,
  UserPlus,
  Users
} from 'lucide-vue-next';
import { useFamily } from '@/composables/useFamily';
import { useNavigation } from '@/composables/useNavigation';
import type { Child, Friend, PlayRequest } from '@/types/domain';

const {
  addChild,
  addFriendByInviteCode,
  addManualFriend,
  activeChildren,
  childInviteCode,
  children,
  confirmedPlayRequests,
  family,
  friends,
  pendingMessages,
  pendingSenderParentPlayRequests,
  removeChild,
  removeFriend,
  selectChild,
  selectedChild,
  setChildActive,
  setFriendActive,
  updateChild,
  updateFriend,
  updateFamilyName
} = useFamily();
const { navigate } = useNavigation();

type ChildEditForm = {
  firstName: string;
  birthDate: string;
  avatar: string;
  favoriteColor: string;
  profileImageUrl: string | null;
  pin: string;
  isActive: boolean;
};

type FriendEditForm = {
  displayName: string;
  birthDate: string;
  avatar: string;
  houseColor: string;
  invitationCode: string;
  invitationLink: string;
  qrCodePayload: string;
  status: Friend['status'];
};

type ParentPanel = 'children' | 'friends' | 'calendar' | 'family' | 'approvals';

const familyName = ref(family.value.name);
const childForm = ref({
  firstName: '',
  birthDate: '',
  avatar: 'heart-house',
  favoriteColor: '#fb7185',
  profileImageUrl: null as string | null,
  pin: ''
});
const childEditForms = ref<Record<string, ChildEditForm>>({});
const friendForm = ref({
  displayName: '',
  birthDate: '',
  avatar: 'house-pink',
  houseColor: '#fb7185',
  invitationCode: '',
  invitationLink: '',
  qrCodePayload: ''
});
const friendEditForms = ref<Record<string, FriendEditForm>>({});
const selectedParentChildId = ref(selectedChild.value?.id ?? children.value[0]?.id ?? '');
const friendInviteCode = ref('');
const friendFallbackName = ref('');
const childSaved = ref(false);
const childEditSavedId = ref('');
const friendSaved = ref(false);
const friendEditSavedId = ref('');
const familySaved = ref(false);
const childRemoveNote = ref('');
const friendRemoveNote = ref('');
const qrReadError = ref('');
const qrScanStatus = ref('');
const qrScannerActive = ref(false);
const qrScannerVideo = ref<HTMLVideoElement | null>(null);
const qrScannerStream = ref<MediaStream | null>(null);
const qrScannerFrameId = ref(0);
const copiedChildId = ref('');
const copiedInviteLinkChildId = ref('');
const qrCodes = ref<Record<string, string>>({});
const calendarMonth = ref(new Date());
const activeParentPanel = ref<ParentPanel>('children');

watch(
  () => family.value.name,
  (name) => {
    familyName.value = name;
  },
  { immediate: true }
);

const selectedParentChild = computed(
  () => children.value.find((child) => child.id === selectedParentChildId.value) ?? children.value[0]
);

const parentFriends = computed(() =>
  friends.value
    .filter((friend) => !selectedParentChildId.value || friend.childId === selectedParentChildId.value)
    .sort((first, second) => {
      if (first.status === second.status) return first.displayName.localeCompare(second.displayName, 'de');
      return first.status === 'active' ? -1 : 1;
    })
);

const selectedParentChildInviteLink = computed(() =>
  selectedParentChild.value ? inviteLinkForChild(selectedParentChild.value) : ''
);

const approvalCount = computed(() => pendingMessages.value.length + pendingSenderParentPlayRequests.value.length);

const monthLabel = computed(() =>
  calendarMonth.value.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })
);

const calendarDays = computed(() => {
  const year = calendarMonth.value.getFullYear();
  const month = calendarMonth.value.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const mondayOffset = (firstOfMonth.getDay() + 6) % 7;
  const startDate = new Date(year, month, 1 - mondayOffset);

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + index);
    return {
      date,
      key: dateKey(date),
      day: date.getDate(),
      inMonth: date.getMonth() === month
    };
  });
});

const upcomingPlayMeetings = computed(() =>
  [...confirmedPlayRequests.value]
    .sort((first, second) => playRequestSortValue(first) - playRequestSortValue(second))
    .slice(0, 5)
);

watchEffect(() => {
  if (!selectedParentChildId.value && children.value[0]) {
    selectedParentChildId.value = children.value[0].id;
  }

  if (selectedParentChildId.value && !children.value.some((child) => child.id === selectedParentChildId.value)) {
    selectedParentChildId.value = children.value[0]?.id ?? '';
  }

  const currentForms = childEditForms.value;
  const nextForms: Record<string, ChildEditForm> = {};
  let formsChanged = Object.keys(currentForms).length !== children.value.length;

  for (const child of children.value) {
    if (!currentForms[child.id]) formsChanged = true;
    nextForms[child.id] = currentForms[child.id] ?? {
      firstName: child.firstName,
      birthDate: child.birthDate,
      avatar: child.avatar,
      favoriteColor: child.favoriteColor,
      profileImageUrl: child.profileImageUrl,
      pin: '',
      isActive: child.isActive !== false
    };
  }

  if (formsChanged) {
    childEditForms.value = nextForms;
  }

  const currentFriendForms = friendEditForms.value;
  const nextFriendForms: Record<string, FriendEditForm> = {};
  let friendFormsChanged = Object.keys(currentFriendForms).length !== friends.value.length;

  for (const friend of friends.value) {
    if (!currentFriendForms[friend.id]) friendFormsChanged = true;
    nextFriendForms[friend.id] = currentFriendForms[friend.id] ?? {
      displayName: friend.displayName,
      birthDate: friend.birthDate ?? '',
      avatar: friend.avatar,
      houseColor: friend.houseColor ?? '#38bdf8',
      invitationCode: friend.invitationCode ?? '',
      invitationLink: friend.invitationLink ?? '',
      qrCodePayload: friend.qrCodePayload ?? '',
      status: friend.status
    };
  }

  if (friendFormsChanged) {
    friendEditForms.value = nextFriendForms;
  }
});

watchEffect(() => {
  for (const child of children.value) {
    const code = childInviteCode(child);
    if (qrCodes.value[child.id]) continue;
    void QRCode.toDataURL(code, {
      margin: 1,
      width: 168,
      color: {
        dark: '#334155',
        light: '#fff7ed'
      }
    }).then((url) => {
      qrCodes.value = { ...qrCodes.value, [child.id]: url };
    });
  }
});

onBeforeUnmount(() => {
  stopQrScanner();
});

async function submitChild() {
  if (!childForm.value.firstName.trim() || !childForm.value.birthDate) return;
  const child = await addChild({
    firstName: childForm.value.firstName,
    birthDate: childForm.value.birthDate,
    avatar: childForm.value.avatar,
    favoriteColor: childForm.value.favoriteColor,
    profileImageUrl: childForm.value.profileImageUrl,
    pin: childForm.value.pin
  });
  selectedParentChildId.value = child.id;
  childForm.value = { firstName: '', birthDate: '', avatar: 'heart-house', favoriteColor: '#fb7185', profileImageUrl: null, pin: '' };
  childSaved.value = true;
  window.setTimeout(() => {
    childSaved.value = false;
  }, 2500);
}

async function submitFamilyName() {
  if (!(await updateFamilyName(familyName.value))) return;
  familyName.value = family.value.name;
  familySaved.value = true;
  window.setTimeout(() => {
    familySaved.value = false;
  }, 2500);
}

function handleChildImage(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file || !file.type.startsWith('image/')) return;

  const reader = new FileReader();
  reader.addEventListener('load', () => {
    childForm.value.profileImageUrl = typeof reader.result === 'string' ? reader.result : null;
  });
  reader.readAsDataURL(file);
}

function handleChildEditImage(child: Child, event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file || !file.type.startsWith('image/')) return;

  const reader = new FileReader();
  reader.addEventListener('load', () => {
    const form = childEditForms.value[child.id];
    if (!form) return;
    childEditForms.value = {
      ...childEditForms.value,
      [child.id]: {
        ...form,
        profileImageUrl: typeof reader.result === 'string' ? reader.result : null
      }
    };
  });
  reader.readAsDataURL(file);
}

async function submitChildEdit(child: Child) {
  const form = childEditForms.value[child.id];
  if (!form) return;

  const previousCode = childInviteCode(child);
  const saved = await updateChild(child.id, form);
  if (!saved) return;

  if (previousCode !== childInviteCode(children.value.find((entry) => entry.id === child.id) ?? child)) {
    const remainingQrCodes = { ...qrCodes.value };
    delete remainingQrCodes[child.id];
    qrCodes.value = remainingQrCodes;
  }

  childEditSavedId.value = child.id;
  window.setTimeout(() => {
    childEditSavedId.value = '';
  }, 2200);
}

async function submitFriend() {
  if (!selectedParentChild.value || !friendForm.value.displayName.trim()) return;
  const saved = await addManualFriend({
    childId: selectedParentChild.value.id,
    displayName: friendForm.value.displayName,
    birthDate: friendForm.value.birthDate || null,
    avatar: friendForm.value.avatar,
    houseColor: friendForm.value.houseColor,
    invitationCode: friendForm.value.invitationCode || null,
    invitationLink: friendForm.value.invitationLink || null,
    qrCodePayload: friendForm.value.qrCodePayload || null,
    source: 'manual'
  });
  if (!saved) return;

  friendForm.value = {
    displayName: '',
    birthDate: '',
    avatar: 'house-pink',
    houseColor: '#fb7185',
    invitationCode: '',
    invitationLink: '',
    qrCodePayload: ''
  };
  friendInviteCode.value = '';
  friendFallbackName.value = '';
  friendSaved.value = true;
  window.setTimeout(() => {
    friendSaved.value = false;
  }, 2500);
}

async function submitFriendFromInviteCode() {
  if (!selectedParentChild.value || !friendInviteCode.value.trim()) return;
  const saved = await addFriendByInviteCode(
    selectedParentChild.value.id,
    extractInvitePayload(friendInviteCode.value.trim()),
    friendFallbackName.value
  );
  if (!saved) return;

  friendInviteCode.value = '';
  friendFallbackName.value = '';
  friendSaved.value = true;
  window.setTimeout(() => {
    friendSaved.value = false;
  }, 2500);
}

function handleFriendQrImage(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  qrReadError.value = '';
  if (!file || !file.type.startsWith('image/')) return;

  const reader = new FileReader();
  reader.addEventListener('load', () => {
    const image = new Image();
    image.addEventListener('load', () => {
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth;
      canvas.height = image.naturalHeight;
      const context = canvas.getContext('2d');
      if (!context) return;

      context.drawImage(image, 0, 0);
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height);

      if (!code?.data) {
        qrReadError.value = 'Kein QR-Code erkannt';
        return;
      }

      applyFriendQrPayload(code.data);
    });
    image.src = typeof reader.result === 'string' ? reader.result : '';
  });
  reader.readAsDataURL(file);
}

async function startQrScanner() {
  qrReadError.value = '';
  qrScanStatus.value = 'Kamera wird gestartet...';
  stopQrScanner();

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: 'environment' } },
      audio: false
    });
    qrScannerStream.value = stream;
    qrScannerActive.value = true;
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, 0);
    });
    if (qrScannerVideo.value) {
      qrScannerVideo.value.srcObject = stream;
      await qrScannerVideo.value.play().catch(() => undefined);
    }
    qrScanStatus.value = 'QR-Code vor die Kamera halten.';
    scanQrFrame();
  } catch {
    qrScannerActive.value = false;
    qrReadError.value = 'Kamera konnte nicht gestartet werden.';
    qrScanStatus.value = '';
  }
}

function stopQrScanner() {
  if (qrScannerFrameId.value) {
    cancelAnimationFrame(qrScannerFrameId.value);
    qrScannerFrameId.value = 0;
  }
  qrScannerStream.value?.getTracks().forEach((track) => track.stop());
  qrScannerStream.value = null;
  qrScannerActive.value = false;
  qrScanStatus.value = '';
}

function setQrScannerVideo(element: Element | ComponentPublicInstance | null) {
  qrScannerVideo.value = element instanceof HTMLVideoElement ? element : null;
}

function scanQrFrame() {
  const video = qrScannerVideo.value;
  if (!video || !qrScannerActive.value) return;

  if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0 && video.videoHeight > 0) {
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height);
      if (code?.data) {
        applyFriendQrPayload(code.data);
        qrScanStatus.value = 'QR-Code erkannt.';
        stopQrScanner();
        return;
      }
    }
  }

  qrScannerFrameId.value = requestAnimationFrame(scanQrFrame);
}

function applyFriendQrPayload(payload: string) {
  const normalizedPayload = extractInvitePayload(payload.trim());
  friendInviteCode.value = normalizedPayload;
  friendForm.value.qrCodePayload = payload.trim();
  friendForm.value.invitationCode = normalizedPayload;
  if (!friendForm.value.displayName.trim()) {
    friendForm.value.displayName = friendNameFromInvitePayload(normalizedPayload);
  }
}

async function submitFriendEdit(friend: Friend) {
  const form = friendEditForms.value[friend.id];
  if (!form) return;

  const saved = await updateFriend(friend.id, {
    displayName: form.displayName,
    birthDate: form.birthDate || null,
    avatar: form.avatar,
    houseColor: form.houseColor,
    invitationCode: form.invitationCode || null,
    invitationLink: form.invitationLink || null,
    qrCodePayload: form.qrCodePayload || null,
    source: friend.source ?? 'manual',
    status: form.status
  });
  if (!saved) return;

  friendEditSavedId.value = friend.id;
  window.setTimeout(() => {
    friendEditSavedId.value = '';
  }, 2200);
}

async function toggleFriend(friend: Friend) {
  friendRemoveNote.value = '';
  const nextActive = friend.status !== 'active';
  if (!nextActive) {
    const confirmed = window.confirm(`${friend.displayName} wirklich deaktivieren?`);
    if (!confirmed) return;
  }

  const changed = nextActive ? await setFriendActive(friend.id, true) : await removeFriend(friend.id);
  if (!changed) return;

  friendRemoveNote.value = nextActive ? `${friend.displayName} ist wieder aktiv.` : `${friend.displayName} wurde deaktiviert.`;
  window.setTimeout(() => {
    friendRemoveNote.value = '';
  }, 3000);
}

async function copyInvite(child: Child) {
  const code = childInviteCode(child);
  copiedChildId.value = child.id;
  await navigator.clipboard?.writeText(code).catch(() => undefined);
  window.setTimeout(() => {
    copiedChildId.value = '';
  }, 1800);
}

async function copyInviteLink(child: Child) {
  const link = inviteLinkForChild(child);
  copiedInviteLinkChildId.value = child.id;
  await navigator.clipboard?.writeText(link).catch(() => undefined);
  window.setTimeout(() => {
    copiedInviteLinkChildId.value = '';
  }, 1800);
}

function openChildWorld(child: Child) {
  selectChild(child);
  navigate('child-home');
}

function openParentPanel(panel: ParentPanel) {
  if (panel === 'approvals') {
    navigate('approvals');
    return;
  }

  activeParentPanel.value = panel;
}

async function deleteChild(child: Child) {
  childRemoveNote.value = '';
  if (child.isActive !== false && activeChildren.value.length <= 1) {
    childRemoveNote.value = 'Das letzte aktive Kind kann nicht deaktiviert werden.';
    return;
  }

  const nextActive = child.isActive === false;
  if (!nextActive) {
    const confirmed = window.confirm(`${child.firstName} wirklich deaktivieren?`);
    if (!confirmed) return;
  }

  const changed = nextActive ? await setChildActive(child.id, true) : await removeChild(child.id);
  if (!changed) return;

  const remainingQrCodes = { ...qrCodes.value };
  delete remainingQrCodes[child.id];
  qrCodes.value = remainingQrCodes;
  selectedParentChildId.value = children.value[0]?.id ?? '';
  childRemoveNote.value = nextActive ? `${child.firstName} ist wieder aktiv.` : `${child.firstName} wurde deaktiviert.`;
  window.setTimeout(() => {
    childRemoveNote.value = '';
  }, 3000);
}

function previousCalendarMonth() {
  calendarMonth.value = new Date(calendarMonth.value.getFullYear(), calendarMonth.value.getMonth() - 1, 1);
}

function nextCalendarMonth() {
  calendarMonth.value = new Date(calendarMonth.value.getFullYear(), calendarMonth.value.getMonth() + 1, 1);
}

function playMeetingsForDay(key: string) {
  return confirmedPlayRequests.value.filter((request) => request.parentAdjustedDate === key);
}

function playMeetingTitle(request: PlayRequest) {
  return `${childName(request.senderChildId)} mit ${friendNameFromChildId(request.receiverChildId)}`;
}

function playMeetingDateText(request: PlayRequest) {
  if (!request.parentAdjustedDate) return 'Datum noch offen';
  return new Date(`${request.parentAdjustedDate}T12:00:00`).toLocaleDateString('de-DE');
}

function playMeetingTimeText(request: PlayRequest) {
  return [request.parentAdjustedTimeRange, request.parentAdjustedExactTime].filter(Boolean).join(' ') || request.requestedTimeLabel;
}

function childName(childId: string) {
  return children.value.find((child) => child.id === childId)?.firstName ?? 'Kind';
}

function friendNameFromChildId(friendChildId: string) {
  return friends.value.find((friend) => friend.friendChildId === friendChildId)?.displayName ?? 'Freund';
}

function playRequestSortValue(request: PlayRequest) {
  if (!request.parentAdjustedDate) return Number.MAX_SAFE_INTEGER;
  return new Date(`${request.parentAdjustedDate}T12:00:00`).getTime();
}

function dateKey(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function inviteLinkForChild(child: Child) {
  const code = childInviteCode(child);
  if (typeof window === 'undefined') return code;
  const url = new URL(window.location.href);
  url.hash = 'parent-settings';
  url.searchParams.set('friendInvite', code);
  return url.toString();
}

function extractInvitePayload(rawPayload: string) {
  if (!rawPayload) return '';
  if (rawPayload.startsWith('HHKID:') || rawPayload.startsWith('child-')) return rawPayload;

  try {
    const url = new URL(rawPayload);
    return (
      url.searchParams.get('friendInvite') ??
      url.searchParams.get('invite') ??
      url.searchParams.get('code') ??
      rawPayload
    );
  } catch {
    return rawPayload;
  }
}

function friendNameFromInvitePayload(payload: string) {
  const parts = payload.split(':');
  if (parts[0] === 'HHKID' && parts.length >= 4) {
    return decodeURIComponent(parts.slice(3).join(':'));
  }
  return '';
}
</script>

<template>
  <section class="mx-auto max-w-7xl px-4 py-6 pb-28 sm:px-6 md:pb-10">
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div>
        <p class="text-sm font-black uppercase tracking-[0.16em] text-heart-leaf">{{ family.name }}</p>
        <h1 class="mt-2 text-4xl font-black text-heart-berry md:text-6xl">Elternbereich</h1>
        <p class="mt-3 max-w-2xl text-lg font-bold leading-7 text-slate-600">
          Waehle eine Kachel und arbeite nur in dem Bereich, den du gerade brauchst.
        </p>
      </div>
    </div>

    <div class="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-5">
      <button
        class="focus-ring min-h-44 rounded-[28px] p-5 text-left shadow-soft transition hover:-translate-y-1"
        :class="activeParentPanel === 'children' ? 'bg-heart-coral text-white' : 'bg-white text-heart-ink'"
        type="button"
        @click="openParentPanel('children')"
      >
        <span class="flex items-center justify-between gap-3">
          <span class="grid h-14 w-14 place-items-center rounded-2xl bg-white/25">
            <Baby class="h-8 w-8" aria-hidden="true" />
          </span>
          <span class="text-4xl font-black">{{ children.length }}</span>
        </span>
        <span class="mt-5 block text-2xl font-black">Kinder</span>
        <span class="mt-2 block text-sm font-bold opacity-80">Anlegen, bearbeiten, Kinderwelt oeffnen</span>
      </button>

      <button
        class="focus-ring min-h-44 rounded-[28px] p-5 text-left shadow-soft transition hover:-translate-y-1"
        :class="activeParentPanel === 'friends' ? 'bg-heart-leaf text-white' : 'bg-white text-heart-ink'"
        type="button"
        @click="openParentPanel('friends')"
      >
        <span class="flex items-center justify-between gap-3">
          <span class="grid h-14 w-14 place-items-center rounded-2xl bg-white/25">
            <UserPlus class="h-8 w-8" aria-hidden="true" />
          </span>
          <span class="text-4xl font-black">{{ friends.length }}</span>
        </span>
        <span class="mt-5 block text-2xl font-black">Freunde</span>
        <span class="mt-2 block text-sm font-bold opacity-80">Freundschaften anlegen und Codes teilen</span>
      </button>

      <button
        class="focus-ring min-h-44 rounded-[28px] p-5 text-left shadow-soft transition hover:-translate-y-1"
        :class="activeParentPanel === 'calendar' ? 'bg-sky-700 text-white' : 'bg-white text-heart-ink'"
        type="button"
        @click="openParentPanel('calendar')"
      >
        <span class="flex items-center justify-between gap-3">
          <span class="grid h-14 w-14 place-items-center rounded-2xl bg-white/25">
            <CalendarDays class="h-8 w-8" aria-hidden="true" />
          </span>
          <span class="text-4xl font-black">{{ upcomingPlayMeetings.length }}</span>
        </span>
        <span class="mt-5 block text-2xl font-black">Kalender</span>
        <span class="mt-2 block text-sm font-bold opacity-80">Spieltreffen sehen und planen</span>
      </button>

      <button
        class="focus-ring min-h-44 rounded-[28px] p-5 text-left shadow-soft transition hover:-translate-y-1"
        :class="activeParentPanel === 'family' ? 'bg-heart-berry text-white' : 'bg-white text-heart-ink'"
        type="button"
        @click="openParentPanel('family')"
      >
        <span class="flex items-center justify-between gap-3">
          <span class="grid h-14 w-14 place-items-center rounded-2xl bg-white/25">
            <Users class="h-8 w-8" aria-hidden="true" />
          </span>
          <span class="text-2xl font-black">Haus</span>
        </span>
        <span class="mt-5 block text-2xl font-black">Familie</span>
        <span class="mt-2 block text-sm font-bold opacity-80">Familiennamen pflegen</span>
      </button>

      <button
        class="focus-ring min-h-44 rounded-[28px] bg-white p-5 text-left text-heart-ink shadow-soft transition hover:-translate-y-1"
        type="button"
        @click="openParentPanel('approvals')"
      >
        <span class="flex items-center justify-between gap-3">
          <span class="grid h-14 w-14 place-items-center rounded-2xl bg-heart-mint text-heart-leaf">
            <ShieldCheck class="h-8 w-8" aria-hidden="true" />
          </span>
          <span class="text-4xl font-black">{{ approvalCount }}</span>
        </span>
        <span class="mt-5 block text-2xl font-black">Freigaben</span>
        <span class="mt-2 block text-sm font-bold text-slate-500">Bilder, Stimmen und Spielanfragen pruefen</span>
      </button>
    </div>

    <div class="mt-6 grid gap-5">
      <div v-if="activeParentPanel === 'family' || activeParentPanel === 'children'" class="grid gap-5">
        <form v-if="activeParentPanel === 'family'" class="rounded-[28px] bg-white p-6 shadow-soft" @submit.prevent="submitFamilyName">
          <div class="flex items-center gap-3">
            <span class="grid h-14 w-14 place-items-center rounded-2xl bg-heart-cream text-heart-coral">
              <Users class="h-8 w-8" aria-hidden="true" />
            </span>
            <h2 class="text-2xl font-black text-heart-ink">Familienname</h2>
          </div>

          <label class="mt-5 block text-sm font-black text-heart-ink">
            Name der Familie
            <input
              v-model="familyName"
              class="focus-ring mt-2 min-h-14 w-full rounded-2xl border border-orange-100 px-4"
              autocomplete="off"
              required
            />
          </label>

          <div class="mt-5 flex flex-wrap items-center gap-3">
            <button class="focus-ring inline-flex min-h-14 items-center gap-2 rounded-2xl bg-heart-leaf px-5 font-black text-white" type="submit">
              <Save class="h-6 w-6" aria-hidden="true" />
              Familienname speichern
            </button>
            <p v-if="familySaved" class="rounded-full bg-heart-mint px-4 py-2 font-black text-heart-leaf">Gespeichert</p>
          </div>
        </form>

        <form v-if="activeParentPanel === 'children'" class="rounded-[28px] bg-white p-6 shadow-soft" @submit.prevent="submitChild">
          <div class="flex items-center gap-3">
            <span class="grid h-14 w-14 place-items-center rounded-2xl bg-heart-mint text-heart-leaf">
              <Baby class="h-8 w-8" aria-hidden="true" />
            </span>
            <h2 class="text-2xl font-black text-heart-ink">Kind anlegen</h2>
          </div>

          <div class="mt-6 grid gap-4 md:grid-cols-2">
            <label class="block text-sm font-black text-heart-ink">
              Name
              <input
                v-model="childForm.firstName"
                class="focus-ring mt-2 min-h-14 w-full rounded-2xl border border-orange-100 px-4"
                autocomplete="off"
                required
              />
            </label>
            <label class="block text-sm font-black text-heart-ink">
              Geburtsdatum
              <span class="relative mt-2 block">
                <Calendar class="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-heart-coral" aria-hidden="true" />
                <input
                  v-model="childForm.birthDate"
                  class="focus-ring min-h-14 w-full rounded-2xl border border-orange-100 px-12"
                  type="date"
                  required
                />
              </span>
            </label>
            <label class="block text-sm font-black text-heart-ink">
              Avatar
              <select v-model="childForm.avatar" class="focus-ring mt-2 min-h-14 w-full rounded-2xl border border-orange-100 px-4">
                <option value="heart-house">Herzhaus</option>
                <option value="sun">Sonne</option>
                <option value="rocket">Rakete</option>
                <option value="star">Stern</option>
              </select>
            </label>
            <label class="block text-sm font-black text-heart-ink">
              Lieblingsfarbe
              <input v-model="childForm.favoriteColor" class="focus-ring mt-2 min-h-14 w-full rounded-2xl border border-orange-100 px-4" type="color" />
            </label>
            <label class="block text-sm font-black text-heart-ink md:col-span-2">
              Optionale PIN
              <input
                v-model="childForm.pin"
                class="focus-ring mt-2 min-h-14 w-full rounded-2xl border border-orange-100 px-4"
                type="password"
                inputmode="numeric"
                autocomplete="new-password"
                placeholder="Leer lassen, wenn keine PIN gewuenscht ist"
              />
            </label>
          </div>

          <label class="mt-5 block rounded-[24px] border-2 border-dashed border-orange-200 bg-heart-cream p-5 text-heart-ink">
            <span class="flex flex-wrap items-center gap-4">
              <span class="grid h-20 w-20 place-items-center overflow-hidden rounded-3xl bg-white text-heart-coral shadow-sm">
                <img v-if="childForm.profileImageUrl" class="h-full w-full object-cover" :src="childForm.profileImageUrl" alt="" />
                <ImagePlus v-else class="h-9 w-9" aria-hidden="true" />
              </span>
              <span>
                <span class="block text-lg font-black">Bild hochladen</span>
                <span class="block text-sm font-bold text-slate-500">Profilbild fuer die Familienuebersicht</span>
              </span>
            </span>
            <input class="sr-only" type="file" accept="image/*" @change="handleChildImage" />
          </label>

          <div class="mt-6 flex flex-wrap items-center gap-3">
            <button class="focus-ring inline-flex min-h-14 items-center gap-2 rounded-2xl bg-heart-coral px-5 font-black text-white" type="submit">
              <Check class="h-6 w-6" aria-hidden="true" />
              Kind speichern
            </button>
            <p v-if="childSaved" class="rounded-full bg-heart-mint px-4 py-2 font-black text-heart-leaf">Gespeichert</p>
          </div>
        </form>

        <section v-if="activeParentPanel === 'children'" class="rounded-[28px] bg-white p-6 shadow-soft">
          <div class="flex items-center gap-3">
            <Users class="h-9 w-9 text-sky-700" aria-hidden="true" />
            <h2 class="text-2xl font-black text-heart-ink">Kinder in dieser Familie</h2>
          </div>

          <div class="mt-5 grid gap-4 md:grid-cols-2">
            <article v-for="child in children" :key="child.id" class="rounded-[24px] bg-heart-cream p-4">
              <div class="flex items-center gap-4">
                <span class="grid h-20 w-20 place-items-center overflow-hidden rounded-3xl text-white" :style="{ backgroundColor: child.favoriteColor }">
                  <img v-if="child.profileImageUrl" class="h-full w-full object-cover" :src="child.profileImageUrl" alt="" />
                  <Baby v-else class="h-10 w-10" aria-hidden="true" />
                </span>
                <span class="min-w-0">
                  <span class="block text-2xl font-black text-heart-ink">{{ child.firstName }}</span>
                  <span class="block text-sm font-bold text-slate-500">{{ new Date(child.birthDate).toLocaleDateString('de-DE') }}</span>
                  <span class="mt-1 inline-flex rounded-full px-3 py-1 text-xs font-black uppercase" :class="child.isActive === false ? 'bg-rose-100 text-rose-800' : 'bg-heart-mint text-heart-leaf'">
                    {{ child.isActive === false ? 'inaktiv' : 'aktiv' }}
                  </span>
                </span>
              </div>

              <div v-if="childEditForms[child.id]" class="mt-5 grid gap-3 rounded-[22px] bg-white p-4">
                <label class="block text-sm font-black text-heart-ink">
                  Name
                  <input
                    v-model="childEditForms[child.id].firstName"
                    class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4"
                    autocomplete="off"
                    required
                  />
                </label>
                <label class="block text-sm font-black text-heart-ink">
                  Geburtstag
                  <span class="relative mt-2 block">
                    <Calendar class="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-heart-coral" aria-hidden="true" />
                    <input
                      v-model="childEditForms[child.id].birthDate"
                      class="focus-ring min-h-12 w-full rounded-2xl border border-orange-100 px-12"
                      type="date"
                      required
                    />
                  </span>
                </label>
                <label class="block text-sm font-black text-heart-ink">
                  Avatar
                  <select v-model="childEditForms[child.id].avatar" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4">
                    <option value="heart-house">Herzhaus</option>
                    <option value="sun">Sonne</option>
                    <option value="rocket">Rakete</option>
                    <option value="star">Stern</option>
                  </select>
                </label>
                <label class="block text-sm font-black text-heart-ink">
                  Lieblingsfarbe
                  <input v-model="childEditForms[child.id].favoriteColor" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" type="color" />
                </label>
                <label class="block text-sm font-black text-heart-ink">
                  Neue PIN optional
                  <input
                    v-model="childEditForms[child.id].pin"
                    class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4"
                    type="password"
                    inputmode="numeric"
                    autocomplete="new-password"
                    placeholder="Leer lassen, um PIN nicht zu aendern"
                  />
                </label>
                <label class="flex items-center gap-3 rounded-2xl bg-heart-cream px-4 py-3 text-sm font-black text-heart-ink">
                  <input v-model="childEditForms[child.id].isActive" class="h-5 w-5 rounded border-orange-200 text-heart-coral" type="checkbox" />
                  Kinderprofil aktiv
                </label>
                <label class="block rounded-[20px] border-2 border-dashed border-orange-200 bg-heart-cream p-4 text-heart-ink">
                  <span class="flex items-center gap-3">
                    <span class="grid h-16 w-16 place-items-center overflow-hidden rounded-2xl bg-white text-heart-coral shadow-sm">
                      <img v-if="childEditForms[child.id].profileImageUrl" class="h-full w-full object-cover" :src="childEditForms[child.id].profileImageUrl ?? ''" alt="" />
                      <ImagePlus v-else class="h-8 w-8" aria-hidden="true" />
                    </span>
                    <span>
                      <span class="block font-black">Bild aendern</span>
                      <span class="block text-sm font-bold text-slate-500">Profilbild aktualisieren</span>
                    </span>
                  </span>
                  <input class="sr-only" type="file" accept="image/*" @change="handleChildEditImage(child, $event)" />
                </label>
                <div class="flex flex-wrap items-center gap-3">
                  <button class="focus-ring inline-flex min-h-12 items-center gap-2 rounded-2xl bg-heart-leaf px-4 font-black text-white" type="button" @click="submitChildEdit(child)">
                    <Save class="h-5 w-5" aria-hidden="true" />
                    Daten speichern
                  </button>
                  <p v-if="childEditSavedId === child.id" class="rounded-full bg-heart-mint px-4 py-2 font-black text-heart-leaf">Gespeichert</p>
                </div>
              </div>

              <div class="mt-4 grid gap-3 sm:grid-cols-2">
                <button class="focus-ring min-h-12 rounded-2xl bg-white px-4 font-black text-heart-ink shadow-sm" type="button" @click="openChildWorld(child)">
                  Welt ansehen
                </button>
                <button
                  class="focus-ring inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-rose-100 px-4 font-black text-rose-800 shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
                  type="button"
                  :disabled="child.isActive !== false && activeChildren.length <= 1"
                  @click="deleteChild(child)"
                >
                  <Trash2 class="h-5 w-5" aria-hidden="true" />
                  {{ child.isActive === false ? 'Aktivieren' : 'Deaktivieren' }}
                </button>
              </div>
            </article>
          </div>
          <p v-if="childRemoveNote" class="mt-4 rounded-2xl bg-white px-4 py-3 font-black text-heart-ink">{{ childRemoveNote }}</p>
        </section>
      </div>

      <aside v-if="activeParentPanel === 'calendar' || activeParentPanel === 'friends'" class="grid gap-5 lg:self-start">
        <section v-if="activeParentPanel === 'calendar'" class="rounded-[28px] bg-white p-6 shadow-soft">
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-3">
              <CalendarDays class="h-9 w-9 text-heart-coral" aria-hidden="true" />
              <h2 class="text-2xl font-black text-heart-ink">Spielkalender</h2>
            </div>
            <div class="flex items-center gap-2">
              <button class="focus-ring grid h-10 w-10 place-items-center rounded-full bg-heart-cream text-heart-ink" type="button" aria-label="Vorheriger Monat" @click="previousCalendarMonth">
                <ChevronLeft class="h-5 w-5" aria-hidden="true" />
              </button>
              <button class="focus-ring grid h-10 w-10 place-items-center rounded-full bg-heart-cream text-heart-ink" type="button" aria-label="Naechster Monat" @click="nextCalendarMonth">
                <ChevronRight class="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          </div>

          <p class="mt-4 text-lg font-black text-heart-berry">{{ monthLabel }}</p>
          <div class="mt-4 grid grid-cols-7 gap-1 text-center text-xs font-black uppercase text-slate-500">
            <span>Mo</span>
            <span>Di</span>
            <span>Mi</span>
            <span>Do</span>
            <span>Fr</span>
            <span>Sa</span>
            <span>So</span>
          </div>
          <div class="mt-2 grid grid-cols-7 gap-1">
            <div
              v-for="day in calendarDays"
              :key="day.key"
              class="min-h-16 rounded-2xl border px-1 py-2 text-center"
              :class="day.inMonth ? 'border-orange-100 bg-heart-cream text-heart-ink' : 'border-slate-100 bg-slate-50 text-slate-300'"
            >
              <span class="text-sm font-black">{{ day.day }}</span>
              <div class="mt-1 flex justify-center gap-1">
                <span
                  v-for="meeting in playMeetingsForDay(day.key)"
                  :key="meeting.id"
                  class="h-2.5 w-2.5 rounded-full bg-heart-coral"
                  :title="playMeetingTitle(meeting)"
                />
              </div>
            </div>
          </div>

          <div class="mt-6">
            <h3 class="text-xl font-black text-heart-ink">Naechste Treffen</h3>
            <div class="mt-3 grid gap-3">
              <p v-if="upcomingPlayMeetings.length === 0" class="rounded-2xl bg-heart-cream px-4 py-3 font-bold text-slate-500">
                Noch keine bestaetigten Spieltreffen.
              </p>
              <article
                v-for="meeting in upcomingPlayMeetings"
                :key="meeting.id"
                class="rounded-2xl bg-heart-cream px-4 py-3"
              >
                <p class="text-lg font-black text-heart-ink">{{ playMeetingTitle(meeting) }}</p>
                <p class="mt-2 inline-flex items-center gap-2 text-sm font-bold text-slate-600">
                  <Calendar class="h-4 w-4 text-heart-coral" aria-hidden="true" />
                  {{ playMeetingDateText(meeting) }}
                </p>
                <p class="mt-1 inline-flex items-center gap-2 text-sm font-bold text-slate-600">
                  <Clock class="h-4 w-4 text-heart-leaf" aria-hidden="true" />
                  {{ playMeetingTimeText(meeting) }}
                </p>
                <p class="mt-1 inline-flex items-center gap-2 text-sm font-bold text-slate-600">
                  <MapPin class="h-4 w-4 text-sky-700" aria-hidden="true" />
                  {{ meeting.parentAdjustedPlace || meeting.requestedPlaceLabel }}
                </p>
              </article>
            </div>
          </div>
        </section>

        <form v-if="activeParentPanel === 'friends'" class="rounded-[28px] bg-white p-6 shadow-soft" @submit.prevent="submitFriend">
          <div class="flex items-center gap-3">
            <UserPlus class="h-9 w-9 text-heart-coral" aria-hidden="true" />
            <h2 class="text-2xl font-black text-heart-ink">Freund manuell anlegen</h2>
          </div>
          <p class="mt-3 text-sm font-bold leading-6 text-slate-500">
            Fuer den Prototyp werden Freunde hier manuell gepflegt. Es gibt keine oeffentliche Suche, keine fremden Kontakte und keine Gruppen.
          </p>

          <div class="mt-5 grid gap-4 md:grid-cols-2">
            <label class="block text-sm font-black text-heart-ink">
              Fuer Kind
              <select v-model="selectedParentChildId" class="focus-ring mt-2 min-h-14 w-full rounded-2xl border border-orange-100 px-4">
                <option v-for="child in activeChildren" :key="child.id" :value="child.id">{{ child.firstName }}</option>
              </select>
            </label>
            <label class="block text-sm font-black text-heart-ink">
              Name des Freundes
              <input
                v-model="friendForm.displayName"
                class="focus-ring mt-2 min-h-14 w-full rounded-2xl border border-orange-100 px-4"
                autocomplete="off"
                required
              />
            </label>
            <label class="block text-sm font-black text-heart-ink">
              Geburtstag optional
              <span class="relative mt-2 block">
                <Calendar class="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-heart-coral" aria-hidden="true" />
                <input v-model="friendForm.birthDate" class="focus-ring min-h-14 w-full rounded-2xl border border-orange-100 px-12" type="date" />
              </span>
            </label>
            <label class="block text-sm font-black text-heart-ink">
              Hausfarbe
              <input v-model="friendForm.houseColor" class="focus-ring mt-2 min-h-14 w-full rounded-2xl border border-orange-100 px-4" type="color" />
            </label>
            <label class="block text-sm font-black text-heart-ink">
              Haus-Avatar
              <select v-model="friendForm.avatar" class="focus-ring mt-2 min-h-14 w-full rounded-2xl border border-orange-100 px-4">
                <option value="house-pink">Rosa Haus</option>
                <option value="house-blue">Blaues Haus</option>
                <option value="house-green">Gruenes Haus</option>
                <option value="house-yellow">Gelbes Haus</option>
                <option value="house-parent-added">Elternhaus</option>
              </select>
            </label>
          </div>

          <div class="mt-5 rounded-[24px] bg-heart-cream p-4">
            <div class="flex items-center gap-3">
              <QrCode class="h-7 w-7 text-heart-coral" aria-hidden="true" />
              <h3 class="text-lg font-black text-heart-ink">Fuer spaeter vorbereitet</h3>
            </div>
            <div class="mt-4 grid gap-3 md:grid-cols-2">
              <label class="block text-sm font-black text-heart-ink">
                Einladungscode
                <input v-model="friendForm.invitationCode" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" placeholder="optional" />
              </label>
              <label class="block text-sm font-black text-heart-ink">
                Einladungslink
                <input v-model="friendForm.invitationLink" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" placeholder="optional" />
              </label>
            </div>
            <label class="mt-4 block rounded-[22px] border-2 border-dashed border-orange-200 bg-white p-4 text-heart-ink">
              <span class="flex items-center gap-3">
                <QrCode class="h-7 w-7 text-heart-coral" aria-hidden="true" />
                <span>
                  <span class="block font-black">QR-Bild importieren</span>
                  <span class="block text-sm font-bold text-slate-500">Der Code wird lokal ausgelesen und direkt uebernommen.</span>
                </span>
              </span>
              <input class="sr-only" type="file" accept="image/*" @change="handleFriendQrImage" />
            </label>
            <div class="mt-4 rounded-[22px] bg-white p-4">
              <div class="flex flex-wrap items-center gap-3">
                <button
                  v-if="!qrScannerActive"
                  class="focus-ring inline-flex min-h-12 items-center gap-2 rounded-2xl bg-heart-leaf px-4 font-black text-white"
                  type="button"
                  @click="startQrScanner"
                >
                  <QrCode class="h-5 w-5" aria-hidden="true" />
                  QR-Code scannen
                </button>
                <button
                  v-else
                  class="focus-ring inline-flex min-h-12 items-center gap-2 rounded-2xl bg-rose-100 px-4 font-black text-rose-800"
                  type="button"
                  @click="stopQrScanner"
                >
                  Scan stoppen
                </button>
                <p v-if="qrScanStatus" class="font-bold text-slate-600">{{ qrScanStatus }}</p>
              </div>
              <video
                v-if="qrScannerActive"
                :ref="setQrScannerVideo"
                class="mt-4 aspect-video w-full rounded-2xl bg-black object-cover"
                muted
                playsinline
              />
            </div>
            <textarea
              v-model="friendForm.qrCodePayload"
              class="focus-ring mt-3 min-h-20 w-full rounded-2xl border border-orange-100 px-4 py-3 text-sm"
              placeholder="QR-Code-Inhalt optional"
            />
            <p v-if="qrReadError" class="mt-3 rounded-2xl bg-rose-100 px-4 py-3 font-black text-rose-800">{{ qrReadError }}</p>
          </div>

          <button class="focus-ring mt-5 inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-2xl bg-heart-leaf px-5 font-black text-white" type="submit">
            <UserPlus class="h-6 w-6" aria-hidden="true" />
            Freund anlegen
          </button>
          <p v-if="friendSaved" class="mt-4 rounded-2xl bg-heart-mint px-4 py-3 text-center font-black text-heart-leaf">Freundschaft angelegt</p>
        </form>

        <section v-if="activeParentPanel === 'friends'" class="rounded-[28px] bg-white p-6 shadow-soft">
          <div class="flex items-center gap-3">
            <QrCode class="h-9 w-9 text-heart-coral" aria-hidden="true" />
            <h2 class="text-2xl font-black text-heart-ink">Einladungscode vorbereiten</h2>
          </div>
          <p class="mt-3 text-sm font-bold leading-6 text-slate-500">
            QR-Code, Einladungscode und Einladungslink koennen direkt uebernommen werden. Es gibt keine Suche nach fremden Kindern.
          </p>
          <label class="mt-4 block text-sm font-black text-heart-ink">
            Kind-ID oder QR-Inhalt
            <textarea
              v-model="friendInviteCode"
              class="focus-ring mt-2 min-h-24 w-full rounded-2xl border border-orange-100 px-4 py-3"
              placeholder="HHKID:..."
            />
          </label>
          <label class="mt-4 block text-sm font-black text-heart-ink">
            Name, falls nur eine ID eingegeben wurde
            <input v-model="friendFallbackName" class="focus-ring mt-2 min-h-14 w-full rounded-2xl border border-orange-100 px-4" />
          </label>
          <button class="focus-ring mt-4 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-heart-cream px-4 font-black text-heart-ink shadow-sm" type="button" @click="submitFriendFromInviteCode">
            <UserPlus class="h-5 w-5" aria-hidden="true" />
            Aus Code als Freund anlegen
          </button>
        </section>

        <section v-if="activeParentPanel === 'friends'" class="rounded-[28px] bg-white p-6 shadow-soft">
          <div class="flex items-center gap-3">
            <QrCode class="h-9 w-9 text-heart-leaf" aria-hidden="true" />
            <h2 class="text-2xl font-black text-heart-ink">Kind-ID teilen</h2>
          </div>

          <div v-if="selectedParentChild" class="mt-5 rounded-[24px] bg-heart-cream p-4">
            <div class="flex items-center gap-3">
              <span class="grid h-14 w-14 place-items-center overflow-hidden rounded-2xl text-white" :style="{ backgroundColor: selectedParentChild.favoriteColor }">
                <img v-if="selectedParentChild.profileImageUrl" class="h-full w-full object-cover" :src="selectedParentChild.profileImageUrl" alt="" />
                <KeyRound v-else class="h-7 w-7" aria-hidden="true" />
              </span>
              <span>
                <span class="block text-xl font-black text-heart-ink">{{ selectedParentChild.firstName }}</span>
                <span class="block text-xs font-black uppercase tracking-[0.14em] text-slate-500">Freundschaftscode</span>
              </span>
            </div>

            <img v-if="qrCodes[selectedParentChild.id]" class="mx-auto mt-5 rounded-2xl bg-white p-3 shadow-sm" :src="qrCodes[selectedParentChild.id]" alt="QR-Code fuer Kind-ID" />
            <code class="mt-5 block break-all rounded-2xl bg-white px-4 py-3 text-sm font-black text-heart-ink">{{ childInviteCode(selectedParentChild) }}</code>
            <code class="mt-3 block break-all rounded-2xl bg-white px-4 py-3 text-xs font-black text-heart-ink">{{ selectedParentChildInviteLink }}</code>
            <button class="focus-ring mt-4 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-white px-4 font-black text-heart-ink shadow-sm" type="button" @click="copyInvite(selectedParentChild)">
              <Copy class="h-5 w-5" aria-hidden="true" />
              {{ copiedChildId === selectedParentChild.id ? 'Kopiert' : 'ID kopieren' }}
            </button>
            <button class="focus-ring mt-3 inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-white px-4 font-black text-heart-ink shadow-sm" type="button" @click="copyInviteLink(selectedParentChild)">
              <Copy class="h-5 w-5" aria-hidden="true" />
              {{ copiedInviteLinkChildId === selectedParentChild.id ? 'Link kopiert' : 'Einladungslink kopieren' }}
            </button>
          </div>
        </section>

        <section v-if="activeParentPanel === 'friends'" class="rounded-[28px] bg-white p-6 shadow-soft">
          <h2 class="text-2xl font-black text-heart-ink">Freunde verwalten</h2>
          <div class="mt-4 grid gap-3">
            <p v-if="parentFriends.length === 0" class="font-bold text-slate-500">Noch keine Freunde fuer dieses Kind.</p>
            <article v-for="friend in parentFriends" :key="friend.id" class="rounded-[24px] bg-heart-cream p-4">
              <div class="flex items-center gap-4">
                <span class="grid h-16 w-16 place-items-center rounded-2xl text-white shadow-sm" :style="{ backgroundColor: friend.houseColor ?? '#38bdf8' }">
                  <UserPlus class="h-8 w-8" aria-hidden="true" />
                </span>
                <span class="min-w-0">
                  <span class="block text-2xl font-black text-heart-ink">{{ friend.displayName }}</span>
                  <span class="block text-sm font-bold text-slate-500">{{ childName(friend.childId) }}</span>
                  <span class="mt-1 inline-flex rounded-full px-3 py-1 text-xs font-black uppercase" :class="friend.status === 'active' ? 'bg-heart-mint text-heart-leaf' : 'bg-rose-100 text-rose-800'">
                    {{ friend.status === 'active' ? 'aktiv' : 'inaktiv' }}
                  </span>
                </span>
              </div>

              <div v-if="friendEditForms[friend.id]" class="mt-4 grid gap-3 rounded-[22px] bg-white p-4">
                <label class="block text-sm font-black text-heart-ink">
                  Name
                  <input v-model="friendEditForms[friend.id].displayName" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" />
                </label>
                <label class="block text-sm font-black text-heart-ink">
                  Geburtstag optional
                  <input v-model="friendEditForms[friend.id].birthDate" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" type="date" />
                </label>
                <label class="block text-sm font-black text-heart-ink">
                  Haus-Avatar
                  <select v-model="friendEditForms[friend.id].avatar" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4">
                    <option value="house-pink">Rosa Haus</option>
                    <option value="house-blue">Blaues Haus</option>
                    <option value="house-green">Gruenes Haus</option>
                    <option value="house-yellow">Gelbes Haus</option>
                    <option value="house-parent-added">Elternhaus</option>
                  </select>
                </label>
                <label class="block text-sm font-black text-heart-ink">
                  Hausfarbe
                  <input v-model="friendEditForms[friend.id].houseColor" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" type="color" />
                </label>
                <label class="block text-sm font-black text-heart-ink">
                  Einladungscode
                  <input v-model="friendEditForms[friend.id].invitationCode" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" />
                </label>
                <label class="block text-sm font-black text-heart-ink">
                  Einladungslink
                  <input v-model="friendEditForms[friend.id].invitationLink" class="focus-ring mt-2 min-h-12 w-full rounded-2xl border border-orange-100 px-4" />
                </label>
                <label class="block text-sm font-black text-heart-ink md:col-span-2">
                  QR-Code Inhalt
                  <textarea v-model="friendEditForms[friend.id].qrCodePayload" class="focus-ring mt-2 min-h-20 w-full rounded-2xl border border-orange-100 px-4 py-3" />
                </label>
                <div class="flex flex-wrap items-center gap-3">
                  <button class="focus-ring inline-flex min-h-12 items-center gap-2 rounded-2xl bg-heart-leaf px-4 font-black text-white" type="button" @click="submitFriendEdit(friend)">
                    <Save class="h-5 w-5" aria-hidden="true" />
                    Freund speichern
                  </button>
                  <button class="focus-ring inline-flex min-h-12 items-center gap-2 rounded-2xl bg-rose-100 px-4 font-black text-rose-800" type="button" @click="toggleFriend(friend)">
                    <Trash2 class="h-5 w-5" aria-hidden="true" />
                    {{ friend.status === 'active' ? 'Deaktivieren' : 'Aktivieren' }}
                  </button>
                  <p v-if="friendEditSavedId === friend.id" class="rounded-full bg-heart-mint px-4 py-2 font-black text-heart-leaf">Gespeichert</p>
                </div>
              </div>
            </article>
            <p v-if="friendRemoveNote" class="rounded-2xl bg-white px-4 py-3 font-black text-heart-ink">{{ friendRemoveNote }}</p>
          </div>
        </section>
      </aside>
    </div>
  </section>
</template>
